import { ArrowUpRight, Cpu, MousePointerClick, LineChart } from "lucide-react";
import { DERIV_AFFILIATE_URL } from "@/lib/socialLinks";

const DEVICE_IMG =
  "https://static.prod-images.emergentagent.com/jobs/d6d87f76-c4a8-410a-8b53-e45e296abac9/images/78da892b00334a649ae88ef5e93859af076d16076b181289f98565d58fb46b75.png";

const PLATFORMS = [
  {
    name: "VTS Desk",
    icon: MousePointerClick,
    tagline: "Beginner-first interface",
    desc: "Our flagship one-click trader. Live charts, simplified contracts, instant execution.",
    href: "/trade",
    primary: true,
  },
  {
    name: "DTrader",
    icon: LineChart,
    tagline: "Deriv's classic options",
    desc: "Trade digital options, multipliers, and accumulators from Deriv's standard interface.",
    href: DERIV_AFFILIATE_URL,
    primary: false,
  },
  {
    name: "DBot",
    icon: Cpu,
    tagline: "Automated strategies",
    desc: "Build trading bots with drag-and-drop blocks — no coding required.",
    href: DERIV_AFFILIATE_URL,
    primary: false,
  },
];

const Platforms = () => {
  return (
    <section
      id="platforms"
      data-testid="platforms-section"
      className="relative py-24 lg:py-32 border-t border-vts-line overflow-hidden"
    >
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.25em] text-vts-blue font-semibold mb-4">
              03 — Platforms
            </div>
            <h2 className="font-heading font-bold text-4xl sm:text-5xl text-white tracking-tighter mb-6">
              Trade anywhere.<br />
              <span className="text-vts-silver">Built on Deriv's rails.</span>
            </h2>
            <p className="text-vts-silver/70 text-base font-body leading-relaxed mb-8">
              Use the VTS Desk for a polished beginner experience, or jump straight
              into Deriv's native platforms — DTrader, DBot, and SmartTrader — all
              tied to the same Deriv account.
            </p>
            <div className="relative">
              <img
                src={DEVICE_IMG}
                alt="VTS on multiple devices"
                className="w-full max-w-md rounded-md border border-vts-line"
              />
              <div className="absolute -inset-6 -z-10 vts-glow-blue rounded-full blur-2xl" />
            </div>
          </div>

          <div className="lg:col-span-7 space-y-4">
            {PLATFORMS.map((p) => {
              const Icon = p.icon;
              return (
                <a
                  key={p.name}
                  href={p.href}
                  target={p.href.startsWith("http") ? "_blank" : undefined}
                  rel="noreferrer"
                  data-testid={`platform-card-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`group flex items-center gap-5 p-6 lg:p-7 rounded-md border transition ${
                    p.primary
                      ? "bg-vts-blue/10 border-vts-blue/40 hover:bg-vts-blue/15"
                      : "vts-card vts-card-hover"
                  }`}
                >
                  <div
                    className={`h-12 w-12 rounded-md flex items-center justify-center shrink-0 ${
                      p.primary
                        ? "bg-vts-blue text-white"
                        : "bg-vts-surface border border-vts-line text-vts-silver"
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-heading font-semibold text-xl text-white tracking-tight">
                        {p.name}
                      </h3>
                      {p.primary && (
                        <span className="text-[10px] uppercase tracking-wider bg-vts-blue text-white px-1.5 py-0.5 rounded font-semibold">
                          Recommended
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-vts-silver/60 uppercase tracking-wider mb-2">
                      {p.tagline}
                    </div>
                    <p className="text-sm text-vts-silver/80 font-body leading-relaxed">
                      {p.desc}
                    </p>
                  </div>
                  <ArrowUpRight className="h-5 w-5 text-vts-silver/50 group-hover:text-vts-blue group-hover:scale-110 transition shrink-0" />
                </a>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Platforms;
